<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useRouter } from 'vue-router';

import ContactDetailsItem from '../ContactDetailsItem.vue';
import InfoField from 'shared/components/ui/InfoField.vue';
import Button from 'shared/components/Button.vue';
import SimpleDivider from '../../../../../v3/components/Divider/SimpleDivider.vue';
import ReferralCodesList from './ReferralCodesList.vue';
import ReferralsAPI from 'dashboard/api/referrals';
import { REFERRAL_CODES_DICT } from 'dashboard/constants/referral_codes_dict';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
});

const router = useRouter();
const { isAdmin } = useAdmin();

// Состояние
const referralInternalName = ref('');
const referralPrefix = ref('');
const referralCodes = ref([]);
const totalInvitees = ref(0);
const referrerConversation = ref(null);
const isLoadingConversation = ref(false);

const contactGetter = useMapGetter('contacts/getContact');
const contact = computed(() => contactGetter.value(props.contactId));
const contactAdditionalAttributes = computed(
  () => contact.value?.additional_attributes || {}
);

// --- Methods ---

const loadReferralCodes = async () => {
  referralCodes.value = [];
  totalInvitees.value = 0;

  if (!props.contactId) return;

  try {
    const response = await ReferralsAPI.getStats(props.contactId);
    referralCodes.value = response.data.codes;
    totalInvitees.value = response.data.total_invitees;
  } catch (err) {
    throw new Error(`loadReferrals failed: ${err.message}`);
  }
};

const sendReferral = async () => {
  if (!props.contactId) return;
  try {
    await ReferralsAPI.create(props.contactId, {
      internal_name: referralInternalName.value,
      prefix: referralPrefix.value,
    });
    referralInternalName.value = '';
    referralPrefix.value = '';
    await loadReferralCodes();
  } catch (err) {
    throw new Error(`Failed to send referral: ${err.message}`);
  }
};

const findReferrerConversation = async () => {
  const telegramId = contactAdditionalAttributes.value.referrer;

  if (!telegramId) {
    referrerConversation.value = null;
    return;
  }

  isLoadingConversation.value = true;

  try {
    const { data } =
      await ReferralsAPI.findReferrerConversationByTelegram(telegramId);
    referrerConversation.value = data.last();
  } catch (error) {
    referrerConversation.value = null;
  } finally {
    isLoadingConversation.value = false;
  }
};

const openReferrerConversation = () => {
  if (referrerConversation.value) {
    router.push({
      name: 'inbox_conversation',
      params: {
        accountId: props.accountId,
        inbox_id: referrerConversation.value.inbox_id,
        conversation_id: referrerConversation.value.id,
      },
    });
  }
};

const referrerActionIcon = computed(() => {
  if (isLoadingConversation.value) {
    return 'i-lucide-loader-2 animate-spin';
  }
  if (referrerConversation.value) {
    return 'i-lucide-message-square';
  }
  return 'i-lucide-message-square-off';
});

const formatCodeView = computed(() => {
  const code = contactAdditionalAttributes.value.referrer_code;

  const label = REFERRAL_CODES_DICT[code];

  return label ? `${code} (${label})` : code;
});

// --- WATCHERS ---

watch(
  () => props.contactId,
  (newId, oldId) => {
    if (newId !== oldId) {
      referrerConversation.value = null;
      referralInternalName.value = '';
      referralPrefix.value = '';
    }

    if (newId) {
      loadReferralCodes();
      findReferrerConversation();
    }
  },
  { immediate: true }
);

watch(
  () => contactAdditionalAttributes.value.referrer,
  newReferrer => {
    if (newReferrer) {
      findReferrerConversation();
    } else {
      referrerConversation.value = null;
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (props.contactId) {
    loadReferralCodes();
    if (contactAdditionalAttributes.value.referrer) {
      findReferrerConversation();
    }
  }
});
</script>

<template>
  <div>
    <div v-if="contactAdditionalAttributes.referrer">
      <ContactDetailsItem compact :title="$t('CONTACT_PANEL.REFERRER.TITLE')" />

      <InfoField
        :text="
          referrerConversation?.contact_name ||
          'Telegram ID: ' + contactAdditionalAttributes.referrer
        "
        :has-action="!!referrerConversation"
        :action-icon="referrerActionIcon"
        @action="openReferrerConversation"
      />

      <ContactDetailsItem compact :title="$t('CONTACT_PANEL.REFERRER.CODE')" />
      <InfoField :text="formatCodeView" />
    </div>
    <p
      v-else
      class="p-3 flex flex-col items-center text-center justify-center h-full"
    >
      {{ $t('CONTACT_PANEL.REFERRER.NOT_FOUND') }}
    </p>
    <div v-if="referralCodes.length > 0">
      <SimpleDivider
        class="my-4 border-t border-slate-200 dark:border-slate-800"
      />
      <ContactDetailsItem
        class="mt-4"
        compact
        :title="$t('CONTACT_PANEL.REFERRAL_CODES_LIST.TITLE')"
      />
      <ReferralCodesList
        :items="referralCodes"
        :total-invitees="totalInvitees"
      />
    </div>

    <div v-if="isAdmin" class="mt-4">
      <SimpleDivider class="border-t border-slate-200 dark:border-slate-800" />
      <ContactDetailsItem
        compact
        :title="$t('CONTACT_PANEL.CREATE_REFERRER_CODE.TITLE')"
      />
      <div class="flex flex-col items-center w-full mt-1">
        <div class="flex items-center w-full gap-2">
          <input
            v-model="referralInternalName"
            type="text"
            class="w-full px-2 py-1 border rounded-md border-slate-200 dark:border-slate-700"
            :placeholder="$t('CONTACT_PANEL.CREATE_REFERRER_CODE.ITERNAL_NAME')"
          />
          <input
            v-model="referralPrefix"
            type="text"
            class="w-full px-2 py-1 border rounded-md border-slate-200 dark:border-slate-700"
            :placeholder="$t('CONTACT_PANEL.CREATE_REFERRER_CODE.PREFIX')"
          />
        </div>
        <Button block @click="sendReferral">
          {{ $t('CONTACT_PANEL.CREATE_REFERRER_CODE.BTN') }}
        </Button>
      </div>
    </div>
  </div>
</template>
